import React from 'react';
import { css } from '@emotion/react';
import t from 'react-translate';

// components
import NvIcon from 'shared/components/nv-icon';
import NvTooltip from 'shared/components/nv-tooltip';
import { config } from '@config/pendo.config.json';

// styles
import { halfSpacing, quarterSpacing, standardSpacing } from 'styles/global_defaults/scaffolding';
import { gray5, gray7, primary } from 'styles/global_defaults/colors';
import VoiceCircleColor from './voice-circle-color';
import { getVoiceName } from './ai-character-utils';

export type Voice = {
  id: string;
  previewAssetPath: string;
  color1: string;
  color2: string;
  color3: string;
  gender: 'male' | 'female';
};

const voiceOptionStyles = css`
  border: 1px solid ${gray5};
  border-radius: ${quarterSpacing}px;

  &.selected, &.focused {
    border-color: ${primary};
    outline: 1px solid ${primary};
    outline-offset: 0;
  }

  .radio-indicator {
    width: ${standardSpacing}px;
    height: ${standardSpacing}px;
    border: 1px solid ${gray5};
    border-radius: 50%;
    color: ${gray7};
  }

  &.selected .radio-indicator {
    border: none;
    background-color: ${primary};
  }
`;

type VoiceOptionProps = {
  voice: Voice;
  isSelected: boolean;
  isPlaying: boolean;
  onSelect: () => void;
  onTogglePreview: () => void;
};

const VoiceOption = ({
  voice,
  isSelected,
  isPlaying,
  onSelect,
  onTogglePreview,
}: VoiceOptionProps) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const [isCardFocused, setIsCardFocused] = React.useState(false);
  const [isPreviewFocused, setIsPreviewFocused] = React.useState(false);
  const previewRef = React.useRef<HTMLDivElement>(null);
  const showPreviewControl = isPlaying || isHovered || isPreviewFocused;

  // When playback stops, sync isPreviewFocused with actual DOM focus: if the
  // element no longer has focus the state is stale (e.g. residual from a mouse
  // click) and should be cleared; if it still has focus (keyboard nav) keep it
  React.useEffect(() => {
    if (!isPlaying && isPreviewFocused && previewRef.current !== document.activeElement) {
      setIsPreviewFocused(false);
    }
  }, [isPlaying, isPreviewFocused]);
  let previewIcon: string | undefined;
  if (showPreviewControl) {
    previewIcon = isPlaying ? 'pause' : 'play';
  }

  const handleCardKeyDown = (event: React.KeyboardEvent) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      onSelect();
    }
  };

  const handleTogglePreview = (event: React.MouseEvent) => {
    // this control lives inside the CustomRadioGroup label; without
    // preventDefault, clicking it would also select the radio
    event.preventDefault();
    event.stopPropagation();
    onTogglePreview();
  };

  return (
    <div
      css={voiceOptionStyles}
      className={`d-flex align-items-center px-2 py-1 ${isSelected ? 'selected' : ''} ${isCardFocused ? 'focused' : ''}`}
      tabIndex={0}
      onKeyDown={handleCardKeyDown}
      onFocus={() => setIsCardFocused(true)}
      onBlur={() => setIsCardFocused(false)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-qa={config.pendo.activities.aiRolePlay.configModal.editCharacterVoiceOption}
      data-qa-id={`${config.pendo.activities.aiRolePlay.configModal.editCharacterVoiceOption}-${voice.id}`}
    >
      <div className='d-flex align-items-center' css={css`gap: ${halfSpacing}px;`}>
        <NvTooltip text={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_VOICE.PREVIEW_VOICE()}>
          <VoiceCircleColor
            ref={previewRef}
            color1={voice.color1}
            color2={voice.color2}
            color3={voice.color3}
            icon={previewIcon}
            onClick={handleTogglePreview}
            onFocus={(e) => { e.stopPropagation(); setIsPreviewFocused(true); setIsCardFocused(false); }}
            onBlur={() => setIsPreviewFocused(false)}
            aria-label={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_VOICE.PREVIEW_VOICE()}
          />
        </NvTooltip>
        <div>
          <div className='text-medium font-weight-bold text-black'>
            {getVoiceName(voice.id)}
          </div>
          <div className='text-small text-gray-2'>
            ({t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_VOICE.GENDER[voice.gender.toUpperCase()]()})
          </div>
        </div>
      </div>
      <div className='radio-indicator d-flex align-items-center justify-content-center ml-auto flex-shrink-0'>
        {isSelected && <NvIcon icon='check' size='xss-smallest' />}
      </div>
    </div>
  );
};

export default VoiceOption;
